import React, { useState, useEffect, useCallback } from 'react';
import {
  Search, Eye, FileText, Calendar, User, Building2,
  Package, CheckCircle, Clock, X,
  ChevronLeft, ChevronRight,
  ThumbsDown, ShieldCheck, ChevronDown, ChevronUp,
  AlertTriangle, RefreshCw
} from 'lucide-react';
import API_BASE_URL from "../config";
import Swal from 'sweetalert2';

export default function ApprovalForVisor() {
  const [mrList, setMrList] = useState([]);
  const [filteredMRs, setFilteredMRs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('all');
  const [selectedMR, setSelectedMR] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [processing, setProcessing] = useState(false);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  // Current logged-in user — used as the Approved By
  const [currentUser, setCurrentUser] = useState(null);

  // Expanded rows
  const [expandedRows, setExpandedRows] = useState(new Set());

  // Per-MR stock results (read-only, no deduction)
  const [mrStockMap, setMrStockMap] = useState({});

  // ─────────────────────────────────────────────
  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    setCurrentUser(user);
    fetchPendingMRs(user);
  }, []);

  useEffect(() => { filterMRs(); }, [searchTerm, departmentFilter, mrList]);

  // positionId → department mapping
  const POSITION_DEPT_MAP = {
    'RMPM': 'RM Whse',
    'ELECTRICAL': 'Electrical',
    'PRODUCTION': 'Production',
    'MECHANICAL': 'Mechanical',
    'LOGISTICS': 'Logistics',
    'MOTORPOOL': 'Motorpool',
    'ADMIN': 'Admin',
    'HR': 'HR',


    'WAREHOUSE': 'Warehouse',
    'OPERATIONS': 'Operations',
    'SAFETY': 'Safety',
    'QA': 'QA',
    'QC': 'QC',
    'HR DEPT': 'Admin/HR/MIS',
    'INVENTORY': 'Admin/HR/MIS',
    'FG': 'FG Whse',
    'PACKAGING': 'Packaging',
    'FABRICATION': 'Fabrication',





  };

  // ─── Fetch pending MRs (filtered by user positionId → department) ────
  const fetchPendingMRs = async (user) => {
    setLoading(true);
    setMrStockMap({});

    const activeUser = user ?? currentUser ?? JSON.parse(localStorage.getItem('user') || '{}');
    const positionId = (activeUser?.positionId || activeUser?.authorized || '').trim().toUpperCase();

    // Try direct department field first, then map from positionId
    const directDept = (activeUser?.department || activeUser?.departmentName || '').trim();
    const mappedDept = POSITION_DEPT_MAP[positionId] || '';
    const userDept = (directDept || mappedDept).toLowerCase();

    try {
      const res = await fetch(`${API_BASE_URL}api/MRHeader`);
      if (!res.ok) { setMrList([]); setFilteredMRs([]); return; }
      const data = await res.json();
      const mrArray = Array.isArray(data) ? data : (data ? [data] : []);

      // status === 1 → pending, filtered to user's mapped department
      const pending = mrArray.filter(mr => {
        if (mr.status !== 1) return false;
        if (!userDept) return true; // no mapping found → show all (safe fallback)
        const mrDept = (mr.department || mr.departmentName || '').trim().toLowerCase();
        return mrDept === userDept;
      });

      const withDetails = await Promise.all(
        pending.map(async (header) => {
          try {
            const dr = await fetch(`${API_BASE_URL}api/MRDetails/by-header/${header.id}`);
            const details = dr.ok ? await dr.json() : [];
            return { ...header, mrDetails: Array.isArray(details) ? details : [] };
          } catch { return { ...header, mrDetails: [] }; }
        })
      );

      setMrList(withDetails);
      setFilteredMRs(withDetails);

      // Auto-check stock (display only — no deduction)
      for (const mr of withDetails) checkStockForMR(mr);
    } catch {
      setMrList([]); setFilteredMRs([]);
    } finally { setLoading(false); }
  };

  // ─── Check stock (READ-ONLY) ──────────────────
  const checkStockForMR = useCallback(async (mr) => {
    const mrId = mr.id;
    setMrStockMap(prev => ({ ...prev, [mrId]: { checking: true, results: [] } }));

    const results = [];
    for (const item of (mr.mrDetails || [])) {
      const requestedQty = Number(item.requestedQty) || 0;
      let matchingRecords = [];

      try {
        const r1 = await fetch(`${API_BASE_URL}api/InventoryData/item/${encodeURIComponent(item.itemCode)}`);
        if (r1.ok) {
          const arr = await r1.json();
          matchingRecords = (Array.isArray(arr) ? arr : []).filter(inv => inv.itemCode === item.itemCode);
        }
        if (matchingRecords.length === 0) {
          const r2 = await fetch(`${API_BASE_URL}api/InventoryData/search?itemCode=${encodeURIComponent(item.itemCode)}`);
          if (r2.ok) {
            const arr = await r2.json();
            matchingRecords = (Array.isArray(arr) ? arr : []).filter(inv => inv.itemCode === item.itemCode);
          }
        }
        if (matchingRecords.length === 0 && item.inventoryDataId) {
          const r3 = await fetch(`${API_BASE_URL}api/InventoryData/${item.inventoryDataId}`);
          if (r3.ok) { const rec = await r3.json(); if (rec) matchingRecords = [rec]; }
        }
      } catch { /* silent */ }

      const available = matchingRecords.reduce((sum, inv) => sum + (Number(inv.quantity) || 0), 0);
      const shortage = Math.max(0, requestedQty - available);
      const status = matchingRecords.length === 0 ? 'not_found'
        : available <= 0 ? 'out_of_stock'
          : shortage > 0 ? 'insufficient'
            : 'ok';

      results.push({
        itemCode: item.itemCode,
        itemDescription: item.itemDescription,
        warehouseCode: item.warehouseCode,
        requestedQty,
        available,
        shortage,
        status,
        mrDetailId: item.id ?? `${item.itemCode}-${item.warehouseCode}`,
      });
    }

    setMrStockMap(prev => ({ ...prev, [mrId]: { checking: false, results } }));
  }, []);

  // ─── Filters ──────────────────────────────────
  const filterMRs = () => {
    let filtered = [...mrList];
    if (searchTerm) {
      filtered = filtered.filter(mr =>
        mr.mrNo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        mr.controlNo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        mr.arInvoiceNo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        mr.requestedByName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        mr.departmentName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        mr.department?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    if (departmentFilter !== 'all') {
      filtered = filtered.filter(mr =>
        mr.departmentName === departmentFilter || mr.department === departmentFilter
      );
    }
    setFilteredMRs(filtered);
    setCurrentPage(1);
  };

  const formatDate = (d) => {
    if (!d) return 'N/A';
    return new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const getUniqueDepartments = () => {
    const depts = mrList.map(mr => mr.department || mr.departmentName).filter(Boolean);
    return [...new Set(depts)].sort();
  };

  const toggleRow = (id) => {
    setExpandedRows(prev => {
      const s = new Set(prev);
      s.has(id) ? s.delete(id) : s.add(id);
      return s;
    });
  };

  // ─── Stock badge (table) ──────────────────────
  const getStockBadge = (mrId) => {
    const stock = mrStockMap[mrId];
    if (!stock) return null;
    if (stock.checking) return (
      <span className="inline-flex items-center gap-1 bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full text-xs font-semibold border border-gray-200 animate-pulse">
        <RefreshCw className="w-3 h-3 animate-spin" />Checking...
      </span>
    );
    const r = stock.results || [];
    if (r.some(x => x.status === 'out_of_stock' || x.status === 'not_found'))
      return <span className="inline-flex items-center gap-1 bg-red-100 text-red-700 px-2 py-0.5 rounded-full text-xs font-bold border border-red-200"><AlertTriangle className="w-3 h-3" />Out of stock</span>;
    if (r.some(x => x.status === 'insufficient'))
      return <span className="inline-flex items-center gap-1 bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full text-xs font-bold border border-amber-200"><AlertTriangle className="w-3 h-3" />Insufficient</span>;
    if (r.length > 0)
      return <span className="inline-flex items-center gap-1 bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full text-xs font-bold border border-emerald-200"><CheckCircle className="w-3 h-3" />Stock OK</span>;
    return null;
  };

  // ─── Open modal ───────────────────────────────
  const handleViewForApproval = async (mrId) => {
    try {
      const [headerRes, detailsRes] = await Promise.all([
        fetch(`${API_BASE_URL}api/MRHeader/${mrId}`),
        fetch(`${API_BASE_URL}api/MRDetails/by-header/${mrId}`),
      ]);
      if (!headerRes.ok) {
        Swal.fire({ icon: 'error', title: 'Not Found', text: 'Could not load MR details', confirmButtonColor: '#1e40af' });
        return;
      }
      const headerData = await headerRes.json();
      const mrDetails = detailsRes.ok ? await detailsRes.json() : [];
      const fullMR = { ...headerData, mrDetails: Array.isArray(mrDetails) ? mrDetails : [] };
      setSelectedMR(fullMR);

      // Re-check stock if not yet done
      if (!mrStockMap[mrId] || mrStockMap[mrId].checking === undefined) {
        checkStockForMR(fullMR);
      }
      setShowModal(true);
    } catch {
      Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to load MR details', confirmButtonColor: '#1e40af' });
    }
  };

  // ─── Activity Feed helper ─────────────────────
  const postActivityFeed = async (mrHeaderId, content) => {
    try {
      await fetch(`${API_BASE_URL}api/ActivityFeed`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          mrHeaderId,
          prHeaderId: null,
          poHeaderId: null,
          quotationId: null,
          fuelHeaderId: null,
          content,
          feedType: 'text',
          createdBy: currentUser?.userId || 1,
          createdByName: currentUser?.username || 'System',
        }),
      });
    } catch (err) { console.warn('Activity feed failed:', err); }
  };

  // ─── APPROVE (Visor) — NO inventory deduction ─
  const handleApproveMR = async () => {
    if (!selectedMR) return;

    const approverName = `${currentUser?.firstName || ''} ${currentUser?.lastName || ''}`.trim() || currentUser?.username || 'Visor';
    const approverId = String(currentUser?.userId || 1);

    const result = await Swal.fire({
      title: 'Confirm approval',
      html: `
        <div class="text-left text-sm">
          <p class="mb-2 font-semibold text-gray-700">Control No: <span class="text-indigo-700 font-mono">${selectedMR.controlNo || selectedMR.mrNo}</span></p>
          <p class="text-gray-600 mb-1">Approved by: <strong>${approverName}</strong></p>
          <p class="text-xs text-gray-400 mt-3">⚠️ Visor approval only — inventory will <u>not</u> be deducted.</p>
        </div>`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#10b981',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Yes, approve!',
      cancelButtonText: 'Cancel',
    });

    if (!result.isConfirmed) return;
    setProcessing(true);

    try {
      const { mrDetails: _d, ...mrHeaderOnly } = selectedMR;

      // Update MR Header → status 3 (Approved), NO inventory touch
      const payload = {
        ...mrHeaderOnly,
        model: mrHeaderOnly.model ?? null,
        status: 3,
        checkBy: approverId,
        checkByName: approverName,
        checkDate: new Date().toISOString(),
        approvedBy: approverId,
        approvedByName: approverName,
        approvedDate: new Date().toISOString(),
      };

      const updateRes = await fetch(`${API_BASE_URL}api/MRHeader/${selectedMR.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!updateRes.ok) throw new Error(await updateRes.text());

      // Log
      await fetch(`${API_BASE_URL}api/Logs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'APPROVE-VISOR',
          module: 'Item Request',
          description: `Visor approved MR #${selectedMR.controlNo || selectedMR.mrNo}. ApprovedBy: ${approverName}. No inventory deducted.`,
          userId: currentUser?.userId,
          username: currentUser?.username,
          fullName: approverName,
        }),
      }).catch(() => { });

      // Activity feed
      await postActivityFeed(
        selectedMR.id,
        `✅ MR approved (Visor) by ${approverName}. No inventory deducted.`
      );

      await Swal.fire({
        icon: 'success',
        title: 'Approved!',
        html: `<strong>${selectedMR.controlNo || selectedMR.mrNo}</strong> has been approved.<br/><span class="text-sm text-gray-500">Approved by: ${approverName}</span>`,
        confirmButtonColor: '#10b981',
        timer: 3000,
        showConfirmButton: false,
      });

      closeModal();
      fetchPendingMRs(currentUser);
    } catch (error) {
      await fetch(`${API_BASE_URL}api/Logs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'APPROVE-VISOR-FAILED',
          module: 'Item Request',
          description: `Failed Visor approval MR #${selectedMR.controlNo || selectedMR.mrNo}. Error: ${error.message}`,
          userId: currentUser?.userId,
          username: currentUser?.username,
          fullName: approverName,
        }),
      }).catch(() => { });

      Swal.fire({
        icon: 'error',
        title: 'Approval failed',
        html: `<pre style="text-align:left;max-height:200px;overflow:auto;font-size:12px">${error.message}</pre>`,
        confirmButtonColor: '#dc2626',
      });
    } finally { setProcessing(false); }
  };

  // ─── REJECT ───────────────────────────────────
  const handleRejectMR = async () => {
    if (!selectedMR) return;

    const result = await Swal.fire({
      title: 'Reject material request?',
      input: 'textarea',
      inputLabel: 'Reason for rejection',
      inputPlaceholder: 'Enter reason...',
      showCancelButton: true,
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Yes, reject',
      cancelButtonText: 'Cancel',
      inputValidator: (v) => { if (!v) return 'Please provide a reason!'; },
    });

    if (!result.isConfirmed) return;
    setProcessing(true);

    const approverName = `${currentUser?.firstName || ''} ${currentUser?.lastName || ''}`.trim() || currentUser?.username || 'Visor';

    try {
      const { mrDetails: _rd, ...mrRejectOnly } = selectedMR;
      const payload = {
        ...mrRejectOnly,
        model: mrRejectOnly.model ?? null,
        status: -1,
        checkBy: String(currentUser?.userId || 1),
        checkByName: approverName,
        checkDate: new Date().toISOString(),
        remarks: `${selectedMR.remarks || ''}\n\n[REJECTED by Visor] ${result.value}`.trim(),
      };

      const updateRes = await fetch(`${API_BASE_URL}api/MRHeader/${selectedMR.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!updateRes.ok) throw new Error(await updateRes.text());

      await fetch(`${API_BASE_URL}api/Logs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'REJECT-VISOR',
          module: 'Item Request',
          description: `Visor rejected MR #${selectedMR.controlNo || selectedMR.mrNo}. Reason: ${result.value}`,
          userId: currentUser?.userId,
          username: currentUser?.username,
          fullName: approverName,
        }),
      }).catch(() => { });

      await postActivityFeed(
        selectedMR.id,
        `❌ MR rejected (Visor) by ${approverName}. Reason: ${result.value}`
      );

      await Swal.fire({
        icon: 'success',
        title: 'Rejected',
        text: `${selectedMR.controlNo || selectedMR.mrNo} has been rejected.`,
        confirmButtonColor: '#dc2626',
        timer: 2000,
        showConfirmButton: false,
      });

      closeModal();
      fetchPendingMRs(currentUser);
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Rejection failed',
        text: error.message,
        confirmButtonColor: '#dc2626',
      });
    } finally { setProcessing(false); }
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedMR(null);
  };

  // ─── Helpers ──────────────────────────────────
  const stockStatusStyle = (s) => ({
    ok: 'bg-emerald-50 border-emerald-300 text-emerald-800',
    insufficient: 'bg-amber-50 border-amber-300 text-amber-800',
    out_of_stock: 'bg-red-50 border-red-300 text-red-800',
    not_found: 'bg-gray-50 border-gray-300 text-gray-500',
  }[s] || 'bg-gray-50 border-gray-300 text-gray-500');

  const stockStatusLabel = (s) => ({
    ok: '✅ OK',
    insufficient: '⚠️ Insufficient',
    out_of_stock: '❌ Out of stock',
    not_found: '❓ Not found',
  }[s] || '—');

  const totalPages = Math.ceil(filteredMRs.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentMRs = filteredMRs.slice(startIndex, startIndex + itemsPerPage);

  const modalStockResults = selectedMR ? (mrStockMap[selectedMR.id]?.results || []) : [];
  const modalStockChecking = selectedMR ? (mrStockMap[selectedMR.id]?.checking ?? false) : false;
  const hasStockIssues = modalStockResults.some(r => r.status !== 'ok');

  // Logged-in user display helpers
  const approverFullName = currentUser
    ? `${currentUser.firstName || ''} ${currentUser.lastName || ''}`.trim() || currentUser.username
    : '';
  const approverInitials = approverFullName
    ? approverFullName.split(' ').map(n => n[0]).slice(0, 2).join('').toUpperCase()
    : '?';
  const approverPosition = currentUser?.positionId || currentUser?.authorized || 'Visor';

  // ─── RENDER ───────────────────────────────────
  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-indigo-50 p-6">
      <div className="max-w-8xl mx-auto">

        {/* ── Header ── */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 mb-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-4">
              <div className="bg-gradient-to-br from-indigo-500 to-indigo-600 p-4 rounded-xl shadow-lg shadow-indigo-200">
                <CheckCircle className="w-8 h-8 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Item Request Approval</h1>
                <p className="text-gray-500 mt-1">
                  Visor view — approve requests without inventory deduction
                </p>
              </div>
            </div>
            <div className="text-right">
              <div className="bg-indigo-100 border-2 border-indigo-300 rounded-xl px-6 py-3">
                <p className="text-xs text-indigo-700 uppercase font-semibold mb-1">Pending</p>
                <p className="text-4xl font-bold text-indigo-900">{mrList.length}</p>
              </div>
            </div>
          </div>

          {/* Logged-in user info strip */}
          {currentUser && (
            <div className="flex items-center gap-3 mb-5 bg-indigo-50 border border-indigo-200 rounded-xl px-4 py-3">
              <div className="bg-indigo-600 rounded-full w-9 h-9 flex items-center justify-center flex-shrink-0">
                <span className="text-white font-bold text-sm">{approverInitials}</span>
              </div>
              <div>
                <p className="text-sm font-bold text-indigo-900">{approverFullName}</p>
                <p className="text-xs text-indigo-600">
                  {approverPosition}
                  {(() => {
                    const pos = (currentUser?.positionId || currentUser?.authorized || '').trim().toUpperCase();
                    const direct = (currentUser?.department || currentUser?.departmentName || '').trim();
                    const mapped = POSITION_DEPT_MAP[pos] || '';
                    const dept = direct || mapped;
                    return dept ? ` · ${dept}` : '';
                  })()} — showing your department only
                </p>
              </div>
              <ShieldCheck className="w-4 h-4 text-indigo-400 ml-auto" />
            </div>
          )}

          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search by Control No, A/R Invoice, Name, Department..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-12 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:border-indigo-500 focus:outline-none transition-all"
              />
            </div>
            <select
              value={departmentFilter}
              onChange={(e) => setDepartmentFilter(e.target.value)}
              className="px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-indigo-500 focus:outline-none bg-white min-w-[200px] transition-all"
            >
              <option value="all">All departments</option>
              {getUniqueDepartments().map(d => <option key={d} value={d}>{d}</option>)}
            </select>
          </div>
        </div>

        {/* ── MR Table ── */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="text-center">
                <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-indigo-500 border-t-transparent mb-4"></div>
                <p className="text-gray-600 font-medium">Loading pending requests...</p>
              </div>
            </div>
          ) : filteredMRs.length === 0 ? (
            <div className="text-center py-20">
              <CheckCircle className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 text-lg font-medium">No pending requests</p>
              <p className="text-gray-400 text-sm mt-2">All material requests have been processed</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gradient-to-r from-indigo-600 to-indigo-700 text-white">
                    <tr>
                      <th className="px-4 py-4 w-12"></th>
                      <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider">Date</th>
                      <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider">Name</th>
                      <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider">Department</th>
                      <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider">Control No.</th>
                      <th className="px-6 py-4 text-center text-xs font-bold uppercase tracking-wider">Items</th>
                      <th className="px-6 py-4 text-center text-xs font-bold uppercase tracking-wider">Stock</th>
                      <th className="px-6 py-4 text-center text-xs font-bold uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {currentMRs.map((mr) => (
                      <React.Fragment key={mr.id}>
                        <tr className="hover:bg-indigo-50 transition-colors">
                          <td className="px-4 py-4 text-center">
                            <button onClick={() => toggleRow(mr.id)} className="text-indigo-400 hover:text-indigo-700 transition-colors">
                              {expandedRows.has(mr.id) ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                            </button>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-2">
                              <Calendar className="w-4 h-4 text-indigo-600" />
                              <span className="text-sm font-medium text-gray-900">{formatDate(mr.mrDate)}</span>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-2">
                              <User className="w-4 h-4 text-indigo-600" />
                              <span className="text-sm font-medium text-gray-900">{mr.requestedByName}</span>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-2">
                              <Building2 className="w-4 h-4 text-indigo-600" />
                              <span className="text-sm font-medium text-gray-900">{mr.department || mr.departmentName || 'N/A'}</span>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <span className="inline-flex items-center gap-1.5 bg-indigo-100 text-indigo-800 px-3 py-1.5 rounded-lg text-xs font-bold border border-indigo-200">
                              <FileText className="w-3.5 h-3.5" />{mr.controlNo || mr.mrNo}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-center">
                            <span className="inline-flex items-center gap-1.5 bg-indigo-100 text-indigo-700 px-3 py-1.5 rounded-lg text-xs font-bold border border-indigo-200">
                              <Package className="w-3.5 h-3.5" />{mr.mrDetails?.length || 0}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-center">{getStockBadge(mr.id)}</td>
                          <td className="px-6 py-4 text-center">
                            <button
                              onClick={() => handleViewForApproval(mr.id)}
                              className="inline-flex items-center gap-2 px-5 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl transition-all shadow-md hover:shadow-lg text-sm font-semibold"
                            >
                              <Eye className="w-4 h-4" />Review
                            </button>
                          </td>
                        </tr>

                        {/* Expanded row */}
                        {expandedRows.has(mr.id) && (
                          <tr>
                            <td colSpan="8" className="bg-indigo-50 px-8 py-4 border-b border-indigo-100">
                              {(mr.mrDetails || []).length === 0 ? (
                                <p className="text-sm text-gray-400 italic text-center py-2">No items found.</p>
                              ) : (
                                <table className="w-full text-sm">
                                  <thead>
                                    <tr className="text-xs text-indigo-700 uppercase border-b border-indigo-200">
                                      <th className="py-2 text-left">Item code</th>
                                      <th className="py-2 text-left">Description</th>
                                      <th className="py-2 text-center">Requested</th>
                                      <th className="py-2 text-center">Available</th>
                                      <th className="py-2 text-center">Shortage</th>
                                      <th className="py-2 text-left">UOM</th>
                                      <th className="py-2 text-left">Warehouse</th>
                                      <th className="py-2 text-center">Stock status</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {(mr.mrDetails || []).map((item, idx) => {
                                      const stockEntry = (mrStockMap[mr.id]?.results || []).find(
                                        r => r.mrDetailId === (item.id ?? `${item.itemCode}-${item.warehouseCode}`)
                                      );
                                      const isChecking = mrStockMap[mr.id]?.checking;
                                      return (
                                        <tr key={idx} className="border-b border-indigo-100 last:border-0">
                                          <td className="py-2 font-mono font-bold text-indigo-900">{item.itemCode}</td>
                                          <td className="py-2 text-gray-700">{item.itemDescription}</td>
                                          <td className="py-2 text-center">
                                            <span className="bg-indigo-100 text-indigo-800 px-2 py-0.5 rounded font-bold">{item.requestedQty}</span>
                                          </td>
                                          <td className="py-2 text-center">
                                            {isChecking ? (
                                              <span className="text-gray-400 text-xs animate-pulse">...</span>
                                            ) : stockEntry ? (
                                              <span className={`font-bold ${stockEntry.available <= 0 ? 'text-red-600' : stockEntry.available < item.requestedQty ? 'text-amber-600' : 'text-emerald-600'}`}>
                                                {stockEntry.available}
                                              </span>
                                            ) : <span className="text-gray-400">—</span>}
                                          </td>
                                          <td className="py-2 text-center">
                                            {stockEntry?.shortage > 0
                                              ? <span className="text-red-600 font-bold text-xs">-{stockEntry.shortage}</span>
                                              : <span className="text-gray-400">—</span>}
                                          </td>
                                          <td className="py-2 text-gray-600">{item.uom}</td>
                                          <td className="py-2 text-gray-600">{item.warehouseCode || '—'}</td>
                                          <td className="py-2 text-center">
                                            {isChecking ? (
                                              <span className="text-xs text-gray-400 animate-pulse">Checking...</span>
                                            ) : stockEntry ? (
                                              <span className={`inline-block text-xs font-semibold px-2 py-0.5 rounded-full border ${stockStatusStyle(stockEntry.status)}`}>
                                                {stockStatusLabel(stockEntry.status)}
                                              </span>
                                            ) : <span className="text-gray-400 text-xs">—</span>}
                                          </td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                              )}
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>

              {filteredMRs.length > 0 && totalPages > 1 && (
                <div className="bg-gray-50 px-6 py-4 border-t border-gray-100 flex items-center justify-between">
                  <p className="text-sm text-gray-600">
                    Showing <span className="font-bold text-gray-900">{startIndex + 1}</span> to{' '}
                    <span className="font-bold text-gray-900">{Math.min(startIndex + itemsPerPage, filteredMRs.length)}</span> of{' '}
                    <span className="font-bold text-gray-900">{filteredMRs.length}</span> requests
                  </p>
                  <div className="flex items-center gap-2">
                    <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}
                      className="px-4 py-2 rounded-lg border border-gray-200 text-sm font-medium disabled:opacity-50 hover:bg-gray-100 flex items-center gap-1">
                      <ChevronLeft className="w-4 h-4" /> Previous
                    </button>
                    {[...Array(totalPages)].map((_, idx) => {
                      const p = idx + 1;
                      if (p === 1 || p === totalPages || (p >= currentPage - 1 && p <= currentPage + 1))
                        return (
                          <button key={p} onClick={() => setCurrentPage(p)}
                            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${currentPage === p ? 'bg-indigo-600 text-white shadow-md' : 'border border-gray-200 hover:bg-gray-100'}`}>
                            {p}
                          </button>
                        );
                      else if (p === currentPage - 2 || p === currentPage + 2)
                        return <span key={p} className="px-2 text-gray-400">...</span>;
                      return null;
                    })}
                    <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}
                      className="px-4 py-2 rounded-lg border border-gray-200 text-sm font-medium disabled:opacity-50 hover:bg-gray-100 flex items-center gap-1">
                      Next <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* ══════════════════════════════════════════
          APPROVAL MODAL (Visor)
      ══════════════════════════════════════════ */}
      {showModal && selectedMR && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fadeIn">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[93vh] overflow-hidden flex flex-col animate-slideUp">

            {/* Modal Header */}
            <div className="bg-gradient-to-r from-indigo-600 to-indigo-700 text-white px-8 py-5 flex-shrink-0">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="bg-white/20 p-3 rounded-xl"><FileText className="w-6 h-6" /></div>
                  <div>
                    <h3 className="text-xl font-bold">Review material request</h3>
                    <div className="flex items-center gap-3 mt-1.5">
                      <span className="bg-white/20 px-3 py-1 rounded-lg font-bold font-mono text-sm">{selectedMR.controlNo || selectedMR.mrNo}</span>
                      <span className="px-3 py-1 rounded-lg text-xs font-bold bg-amber-100 text-amber-700">
                        <Clock className="w-3 h-3 inline mr-1" />Pending
                      </span>
                      <span className="px-3 py-1 rounded-lg text-xs font-bold bg-cyan-100 text-cyan-700">
                        Visor approval
                      </span>
                    </div>
                  </div>
                </div>
                <button onClick={closeModal} className="bg-white/10 hover:bg-white/20 rounded-xl p-2.5 transition-all">
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Modal Body */}
            <div className="overflow-y-auto flex-1 bg-gray-50 p-6 space-y-5">

              {/* Request Info */}
              <div className="bg-white rounded-xl border-2 border-indigo-200 p-5">
                <h4 className="text-base font-bold text-indigo-900 mb-3 flex items-center gap-2">
                  <div className="w-1 h-5 bg-indigo-600 rounded-full"></div>Request information
                </h4>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  <div><p className="text-xs text-gray-500 uppercase font-semibold mb-1">Date</p><p className="text-sm font-bold text-gray-900">{formatDate(selectedMR.mrDate)}</p></div>
                  <div><p className="text-xs text-gray-500 uppercase font-semibold mb-1">Control No.</p><p className="text-sm font-bold text-indigo-900 font-mono">{selectedMR.controlNo || selectedMR.mrNo}</p></div>
                  <div><p className="text-xs text-gray-500 uppercase font-semibold mb-1">A/R Invoice No.</p><p className="text-sm font-bold text-indigo-900 font-mono">{selectedMR.arInvoiceNo || '—'}</p></div>
                  <div><p className="text-xs text-gray-500 uppercase font-semibold mb-1">Requested by</p><p className="text-sm font-bold text-gray-900">{selectedMR.requestedByName}</p></div>
                  <div><p className="text-xs text-gray-500 uppercase font-semibold mb-1">Department</p><p className="text-sm font-bold text-gray-900">{selectedMR.department || selectedMR.departmentName || 'N/A'}</p></div>
                  {selectedMR.branchSpecify && (
                    <div><p className="text-xs text-gray-500 uppercase font-semibold mb-1">Branch</p><p className="text-sm font-bold text-gray-900">{selectedMR.branchSpecify}</p></div>
                  )}
                </div>
              </div>

              {selectedMR.remarks && (
                <div className="bg-amber-50 border-l-4 border-amber-500 rounded-xl p-4">
                  <p className="text-xs text-amber-700 uppercase font-bold mb-1">Remarks</p>
                  <p className="text-sm text-gray-800">{selectedMR.remarks}</p>
                </div>
              )}

              {/* Items Table (READ-ONLY, no qty edit) */}
              <div className="bg-white rounded-xl border-2 border-indigo-200 overflow-hidden">
                <div className="px-5 py-4 border-b border-indigo-100 flex items-center justify-between">
                  <h4 className="text-base font-bold text-indigo-900 flex items-center gap-2">
                    <Package className="w-5 h-5 text-indigo-600" />
                    Items &amp; stock status
                    <span className="text-xs text-gray-400">({selectedMR.mrDetails?.length || 0} items)</span>
                  </h4>
                  {/* Visor notice: read-only */}
                  <span className="inline-flex items-center gap-1.5 bg-cyan-50 text-cyan-700 px-3 py-1.5 rounded-lg text-xs font-bold border border-cyan-200">
                    Read-only — no deduction
                  </span>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-gray-50 border-b border-gray-200">
                        <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase">Item code</th>
                        <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase">Description</th>
                        <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase">Warehouse</th>
                        <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase">UOM</th>
                        <th className="px-4 py-3 text-center text-xs font-bold text-gray-700 uppercase">Requested qty</th>
                        <th className="px-4 py-3 text-center text-xs font-bold text-gray-700 uppercase">Available</th>
                        <th className="px-4 py-3 text-center text-xs font-bold text-gray-700 uppercase">Shortage</th>
                        <th className="px-4 py-3 text-center text-xs font-bold text-gray-700 uppercase">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {(selectedMR.mrDetails || []).map((item, index) => {
                        const key = item.id ?? `${item.itemCode}-${item.warehouseCode}`;
                        const stockEntry = modalStockResults.find(r => r.mrDetailId === key);
                        const rowBg = !stockEntry ? '' : stockEntry.status === 'insufficient' ? 'bg-amber-50/40' : stockEntry.status !== 'ok' ? 'bg-red-50/40' : '';

                        return (
                          <tr key={item.id || index} className={`${rowBg} hover:bg-gray-50 transition-colors`}>
                            <td className="px-4 py-3 font-mono font-bold text-indigo-900 text-xs">{item.itemCode}</td>
                            <td className="px-4 py-3 text-gray-700 text-xs max-w-[150px] truncate">{item.itemDescription}</td>
                            <td className="px-4 py-3 text-xs text-gray-600">{item.warehouseCode || '—'}</td>
                            <td className="px-4 py-3 text-xs text-gray-600">{item.uom || '—'}</td>
                            <td className="px-4 py-3 text-center">
                              <span className="bg-indigo-100 text-indigo-800 px-2 py-0.5 rounded font-bold text-xs">{item.requestedQty}</span>
                            </td>
                            <td className="px-4 py-3 text-center">
                              {modalStockChecking ? (
                                <span className="text-gray-400 text-xs animate-pulse">...</span>
                              ) : stockEntry ? (
                                <span className={`font-bold ${stockEntry.available <= 0 ? 'text-red-600' : stockEntry.available < item.requestedQty ? 'text-amber-600' : 'text-emerald-600'}`}>
                                  {stockEntry.available}
                                </span>
                              ) : <span className="text-gray-400">—</span>}
                            </td>
                            <td className="px-4 py-3 text-center">
                              {stockEntry?.shortage > 0
                                ? <span className="text-red-600 font-bold text-xs">-{stockEntry.shortage}</span>
                                : <span className="text-gray-400 text-xs">—</span>}
                            </td>
                            <td className="px-4 py-3 text-center">
                              {modalStockChecking ? (
                                <span className="text-xs text-gray-400 animate-pulse">Checking...</span>
                              ) : stockEntry ? (
                                <span className={`inline-block text-xs font-semibold px-2.5 py-1 rounded-full border ${stockStatusStyle(stockEntry.status)}`}>
                                  {stockStatusLabel(stockEntry.status)}
                                </span>
                              ) : <span className="text-gray-400 text-xs">—</span>}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                {hasStockIssues && !modalStockChecking && (
                  <div className="mx-5 mb-4 mt-3 bg-amber-50 border border-amber-200 rounded-xl p-4 flex gap-3">
                    <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-amber-800">
                      Some items have stock issues. As Visor, you can still approve — inventory will <strong>not</strong> be deducted at this stage.
                    </p>
                  </div>
                )}
              </div>

              {/* ── Approved By (logged-in user) + Signature ── */}
              <div className="bg-white rounded-xl border-2 border-blue-200 p-5">
                <h4 className="text-base font-bold text-blue-900 mb-4 flex items-center gap-2">
                  <div className="w-1 h-5 bg-blue-600 rounded-full"></div>
                  <ShieldCheck className="w-4 h-4 text-blue-600" />Approved by
                  <span className="text-xs text-blue-500 bg-blue-50 px-2 py-0.5 rounded-full border border-blue-200 ml-1">Auto — logged in user</span>
                </h4>

                <div className="flex flex-col md:flex-row gap-5 items-start">
                  {/* User card */}
                  <div className="flex items-center gap-3 bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 flex-1">
                    <div className="bg-blue-600 rounded-full w-12 h-12 flex items-center justify-center flex-shrink-0">
                      <span className="text-white font-bold text-base">{approverInitials}</span>
                    </div>
                    <div>
                      <p className="text-sm font-bold text-blue-900">{approverFullName || '—'}</p>
                      <p className="text-xs text-blue-600 font-medium">{approverPosition}</p>
                      {currentUser?.username && (
                        <p className="text-xs text-blue-400 mt-0.5">@{currentUser.username}</p>
                      )}
                    </div>
                    <CheckCircle className="w-5 h-5 text-blue-500 ml-auto" />
                  </div>

                  {/* Signature box */}
                  <div className="flex flex-col items-center gap-2 min-w-[180px]">
                    <p className="text-xs text-gray-500 uppercase font-semibold">Signature</p>
                    <div className="w-full border-2 border-dashed border-blue-200 rounded-xl bg-blue-50 flex flex-col items-center justify-end"
                      style={{ height: '100px', minWidth: '180px' }}>
                      {/* Stylized signature using the user's name */}
                      <p className="text-blue-700 text-lg mb-3"
                        style={{ fontFamily: 'cursive', letterSpacing: '0.05em', opacity: 0.8 }}>
                        {approverFullName || '—'}
                      </p>
                    </div>
                    <div className="w-full border-t-2 border-gray-400 pt-1 text-center">
                      <p className="text-xs text-gray-600 font-semibold">{approverFullName || '—'}</p>
                      <p className="text-xs text-gray-400">{approverPosition}</p>
                    </div>
                  </div>
                </div>

                {/* Notice: no Released By */}
                <div className="mt-4 bg-cyan-50 border border-cyan-200 rounded-xl px-4 py-3 flex items-center gap-2 text-xs text-cyan-700">
                  <CheckCircle className="w-4 h-4 text-cyan-500 flex-shrink-0" />
                  <span>Released By field is not applicable for Visor approval. No inventory deduction will be made.</span>
                </div>
              </div>

            </div>

            {/* Modal Footer */}
            <div className="flex items-center justify-between px-8 py-4 border-t border-gray-200 bg-white flex-shrink-0">
              <button onClick={closeModal}
                className="px-5 py-2.5 bg-gray-500 hover:bg-gray-600 text-white rounded-xl font-semibold transition-all flex items-center gap-2 text-sm">
                <X className="w-4 h-4" />Cancel
              </button>
              <div className="flex items-center gap-3">
                <button onClick={handleRejectMR} disabled={processing}
                  className="px-5 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-xl font-semibold transition-all flex items-center gap-2 disabled:opacity-50 text-sm">
                  {processing
                    ? <><div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>Processing...</>
                    : <><ThumbsDown className="w-4 h-4" />Reject</>}
                </button>
                <button onClick={handleApproveMR} disabled={processing || modalStockChecking}
                  className="px-7 py-2.5 bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800 text-white rounded-xl font-bold transition-all flex items-center gap-2 text-sm shadow-lg shadow-emerald-200 disabled:opacity-50 disabled:cursor-not-allowed">
                  {processing ? (
                    <><div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>Approving...</>
                  ) : modalStockChecking ? (
                    <><RefreshCw className="w-4 h-4 animate-spin" />Checking stock...</>
                  ) : (
                    <><CheckCircle className="w-4 h-4" />Approve (Visor)</>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        @keyframes fadeIn  { from { opacity: 0; }                          to { opacity: 1; } }
        @keyframes slideUp { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
        .animate-fadeIn  { animation: fadeIn  0.2s ease-out; }
        .animate-slideUp { animation: slideUp 0.3s ease-out; }
      `}</style>
    </div>
  );
}